import React from 'react';

/**
 * ActiveJDContext Component
 *
 * Shows the currently active JD context strip across screens.
 * Provides visibility into what the user is working on.
 *
 * Usage:
 * <ActiveJDContext
 *   jd={{ title: "Senior Java Developer", id: "jd-001" }}
 *   stats={{ profiles: 12, matches: 40, suggested: 6, autoAssigned: 2 }}
 *   onViewMatches={() => navigate('/matches')}
 *   onChangeJD={() => setShowJDSelector(true)}
 * />
 */

export const ActiveJDContext = ({
  jd,
  stats = {},
  onViewMatches,
  onChangeJD,
  onRunAutoMatch,
  compact = false,
  showActions = true,
}) => {
  if (!jd) {
    return (
      <div className="bg-muted border border-line rounded-xl px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-subtle flex items-center justify-center">
            <svg className="w-4 h-4 text-ink-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <span className="text-ink-subtle text-sm">No active JD selected</span>
        </div>
        {onChangeJD && (
          <button
            onClick={onChangeJD}
            className="text-sm text-blue-600 hover:text-blue-300 font-medium"
          >
            Select JD
          </button>
        )}
      </div>
    );
  }

  const { profiles = 0, matches = 0, suggested = 0, autoAssigned = 0 } = stats;
  const hasMatches = matches > 0;
  const needsAutoMatch = profiles > 0 && matches === 0;

  if (compact) {
    return (
      <div className="bg-accent-soft border border-blue-500/20 rounded-lg px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 rounded bg-accent-soft flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-blue-600 font-medium uppercase tracking-wide">Active JD:</span>
            <span className="font-semibold text-ink text-sm">{jd.title}</span>
          </div>
          <div className="flex items-center gap-3 ml-4 text-xs text-blue-400">
            <span>{profiles} profiles</span>
            <span className="text-blue-300">|</span>
            <span>{matches} matches</span>
          </div>
        </div>
        {onChangeJD && (
          <button onClick={onChangeJD} className="text-xs text-blue-600 hover:text-blue-300 underline">
            Change
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-accent-soft border border-blue-500/20 rounded-xl p-4">
      <div className="flex items-center justify-between">
        {/* JD Info */}
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-sm">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-blue-600 font-medium uppercase tracking-wide">Active JD</span>
              {jd.status && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  jd.status === 'active' ? 'bg-green-500/15 text-green-400' :
                  jd.status === 'draft' ? 'bg-subtle text-ink-muted' :
                  'bg-subtle text-ink-muted'
                }`}>
                  {jd.status}
                </span>
              )}
            </div>
            <h3 className="font-semibold text-ink text-lg">{jd.title}</h3>
            {jd.department && (
              <p className="text-sm text-ink-subtle">{jd.department}</p>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-ink">{profiles}</div>
            <div className="text-xs text-ink-subtle">Profiles</div>
          </div>
          <div className="w-px h-8 bg-subtle" />
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{matches}</div>
            <div className="text-xs text-ink-subtle">Matches</div>
          </div>
          <div className="w-px h-8 bg-subtle" />
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{suggested}</div>
            <div className="text-xs text-ink-subtle">Suggested</div>
          </div>
          {autoAssigned > 0 && (
            <>
              <div className="w-px h-8 bg-subtle" />
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{autoAssigned}</div>
                <div className="text-xs text-ink-subtle">Auto-Assigned</div>
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex items-center gap-2">
            {needsAutoMatch && onRunAutoMatch && (
              <button
                onClick={onRunAutoMatch}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Run AutoMatch
              </button>
            )}
            {hasMatches && onViewMatches && (
              <button
                onClick={onViewMatches}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                View Matches
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            )}
            {onChangeJD && (
              <button
                onClick={onChangeJD}
                className="px-4 py-2 bg-elevated text-ink-muted rounded-lg text-sm font-medium hover:bg-muted transition-colors border border-line"
              >
                Change JD
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActiveJDContext;
